import { Modal, message } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CertificateAPI from '../../certificate/CertificateAPI';
import OpenClassAPI from '../OpenClassAPI';
import {
  OpenClass,
  OpenClassCertificateOffering,
  OpenClassRegistration,
} from '../types/OpenClass';
import { CertificateLanguage } from '../../certificate/types/Certificate';
import { canIssueCertificate as registrationCanIssueCertificate } from '../utils/certificateEligibility';
import { OpenClassCertificateEmailResult } from '../types/certificateEmail';
import { reportCertificateEmailOutcome } from '../utils/certificateEmailOutcome';
import { certificateTypeLabel } from '../utils/certificateTypeLabel';
import { BesaccCertificatePreview } from '../components/BesaccCertificatePreviewModal';

// The least an attendee row has to carry for any of these to work, so both the session
// list and the class wide one qualify.
export type CertificateActionAttendee = {
  certificateIssued: boolean;
  certificateSentAt?: string | null;
  name: string;
  registrationId: OpenClassRegistration['id'];
};

const standardTemplateOf = (openClass: OpenClass) =>
  openClass.certificateTemplates.find(({ type }) => type === 'standard');

const certificateLanguageFor = (
  template: OpenClassCertificateOffering,
  chosen?: CertificateLanguage['id'],
) => (template.type === 'standard' ? chosen : undefined);

export const useAttendeeCertificateActions = ({
  clearSelection,
  openClass,
  registrations,
  onUpdated,
}: {
  clearSelection: () => void;
  openClass: OpenClass;
  registrations: OpenClassRegistration[];
  onUpdated: () => Promise<void> | void;
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [languagePrompt, setLanguagePrompt] = useState<{
    kind: 'download' | 'email';
    registrationIds: number[];
    resend: boolean;
  }>();

  const eligibleRegistrationIds = useMemo(() => {
    const canceledSessionIds = new Set(
      openClass.sessions
        .filter(({ status }) => status === 'canceled')
        .map(({ id }) => id),
    );
    return new Set(
      registrations
        .filter((registration) =>
          registrationCanIssueCertificate({
            ...registration,
            sessionAttendances: (registration.sessionAttendances ?? []).filter(
              ({ sessionId }) => !canceledSessionIds.has(sessionId),
            ),
          }),
        )
        .map(({ id }) => id),
    );
  }, [openClass.sessions, registrations]);

  const canIssueCertificate = useCallback(
    (attendee: CertificateActionAttendee) =>
      eligibleRegistrationIds.has(attendee.registrationId),
    [eligibleRegistrationIds],
  );

  const runCertificateAction = useCallback(
    async (
      kind: 'download' | 'email',
      registrationIds: number[],
      resend = false,
      languageId?: CertificateLanguage['id'],
    ) => {
      const ids = new Set(registrationIds);
      const groups = new Map<number, number[]>();
      registrations.forEach(({ certificateTemplateId, id }) => {
        if (!ids.has(id)) return;
        const group = groups.get(certificateTemplateId);
        if (group) {
          group.push(id);
        } else {
          groups.set(certificateTemplateId, [id]);
        }
      });
      const templates = openClass.certificateTemplates.filter(({ id }) =>
        groups.has(id),
      );

      setSaving(true);
      const emailResults: OpenClassCertificateEmailResult[] = [];
      let delivered = false;
      try {
        if (kind === 'download') {
          const baseFileName = t(
            'components.OpenClassAttendeeActions.certificatesFileName',
          );
          for (const template of templates) {
            await CertificateAPI.getOpenClassCertificateZip(
              templates.length > 1
                ? `${baseFileName} ${certificateTypeLabel(template.type)}`
                : baseFileName,
              openClass.id,
              groups.get(template.id) ?? [],
              template.id,
              certificateLanguageFor(template, languageId),
            );
          }
        } else {
          for (const template of templates) {
            emailResults.push(
              ...(await CertificateAPI.emailOpenClassCertificates(
                openClass.id,
                groups.get(template.id) ?? [],
                template.id,
                certificateLanguageFor(template, languageId),
                resend,
              )),
            );
          }
          reportCertificateEmailOutcome(emailResults, t);
        }
        delivered = true;
      } catch {
        if (kind === 'download') {
          message.error(t('views.OpenClasses.downloadCertificatesFailed'));
        } else {
          reportCertificateEmailOutcome(emailResults, t, true);
        }
      } finally {
        await onUpdated();
        if (delivered) clearSelection();
        setSaving(false);
      }
    },
    [clearSelection, onUpdated, openClass, registrations, t],
  );

  const startCertificateAction = useCallback(
    (kind: 'download' | 'email', registrationIds: number[], resend = false) => {
      const standard = standardTemplateOf(openClass);
      const ids = new Set(registrationIds);
      const needsLanguage =
        standard != null &&
        standard.languages.length > 1 &&
        registrations.some(
          ({ certificateTemplateId, id }) =>
            ids.has(id) && certificateTemplateId === standard.id,
        );

      if (needsLanguage) {
        setLanguagePrompt({ kind, registrationIds, resend });
        return;
      }
      void runCertificateAction(
        kind,
        registrationIds,
        resend,
        standard?.languages[0]?.id,
      );
    },
    [openClass, registrations, runCertificateAction],
  );

  const confirmLanguagePrompt = useCallback(
    (languageId: CertificateLanguage['id']) => {
      if (!languagePrompt) return;
      const { kind, registrationIds, resend } = languagePrompt;
      setLanguagePrompt(undefined);
      void runCertificateAction(kind, registrationIds, resend, languageId);
    },
    [languagePrompt, runCertificateAction],
  );

  // Settled rather than raced, so one rejected attendee doesn't drop the rest of the batch.
  const runUpdates = useCallback(
    async (updates: Promise<unknown>[]) => {
      setSaving(true);
      try {
        const results = await Promise.allSettled(updates);
        const failed = results.filter(
          ({ status }) => status === 'rejected',
        ).length;
        if (failed > 0) {
          message.error(
            t('components.OpenClassAttendeeActions.updateFailed', {
              count: failed,
            }),
          );
        }
        await onUpdated();
      } finally {
        setSaving(false);
      }
    },
    [onUpdated, t],
  );

  const downloadCertificates = useCallback(
    (attendees: CertificateActionAttendee[]) => {
      const downloadable = attendees.filter(canIssueCertificate);
      if (downloadable.length === 0) return;

      startCertificateAction(
        'download',
        downloadable.map(({ registrationId }) => registrationId),
      );
    },
    [canIssueCertificate, startCertificateAction],
  );

  const runBesaccChange = useCallback(
    async (
      request: Promise<unknown>,
      successKey: 'removed' | 'uploaded',
      failKey: 'removeFailed' | 'uploadFailed',
    ) => {
      setSaving(true);
      try {
        await request;
        await onUpdated();
        message.success(t(`components.BesaccCertificatePreview.${successKey}`));
        return true;
      } catch {
        message.error(t(`components.BesaccCertificatePreview.${failKey}`));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [onUpdated, t],
  );

  const uploadBesaccCertificate = useCallback(
    (registrationId: number, file: File) =>
      runBesaccChange(
        OpenClassAPI.uploadBesaccCertificate(registrationId, file),
        'uploaded',
        'uploadFailed',
      ),
    [runBesaccChange],
  );

  const removeBesaccCertificate = useCallback(
    (registrationId: number) =>
      new Promise<boolean>((resolve) => {
        Modal.confirm({
          content: t('components.BesaccCertificatePreview.removeConfirm'),
          okButtonProps: { danger: true },
          okText: t('components.BesaccCertificatePreview.remove'),
          title: t('components.BesaccCertificatePreview.removeTitle'),
          onCancel: () => resolve(false),
          onOk: async () =>
            resolve(
              await runBesaccChange(
                OpenClassAPI.deleteBesaccCertificate(registrationId),
                'removed',
                'removeFailed',
              ),
            ),
        });
      }),
    [runBesaccChange, t],
  );

  const [besaccPreviewId, setBesaccPreviewId] = useState<number>();
  const besaccPreview = useMemo((): BesaccCertificatePreview | undefined => {
    const registration = registrations.find(({ id }) => id === besaccPreviewId);
    if (!registration?.besaccCertificateFileName) return undefined;
    return {
      attendeeName: registration.attendee.name,
      fileName: registration.besaccCertificateFileName,
      registrationId: registration.id,
      sentAt: registration.certificateSentAt,
    };
  }, [besaccPreviewId, registrations]);

  // Only the single attendee action resends, a bulk send leaves out anyone already served
  const emailable = useCallback(
    (attendees: CertificateActionAttendee[], resend: boolean) =>
      attendees.filter(
        (attendee) =>
          canIssueCertificate(attendee) &&
          (resend || !attendee.certificateSentAt),
      ),
    [canIssueCertificate],
  );

  const emailAttendeeCertificates = useCallback(
    (attendees: CertificateActionAttendee[], resend = false) => {
      const eligible = emailable(attendees, resend);
      if (eligible.length === 0) return;
      startCertificateAction(
        'email',
        eligible.map(({ registrationId }) => registrationId),
        resend,
      );
    },
    [emailable, startCertificateAction],
  );

  // Sits right under download and sends for real, so ask first
  const confirmEmailCertificate = useCallback(
    (attendee: CertificateActionAttendee) => {
      Modal.confirm({
        content: attendee.certificateSentAt
          ? t(
              'components.OpenClassAttendeeActions.emailCertificateResendConfirm',
              {
                date: dayjs(attendee.certificateSentAt).format(
                  'YYYY-MM-DD HH:mm',
                ),
                name: attendee.name,
              },
            )
          : t('components.OpenClassAttendeeActions.emailCertificateConfirm', {
              name: attendee.name,
            }),
        okText: t('common.confirm'),
        title: t('components.OpenClassAttendeeActions.emailCertificate'),
        onOk: () =>
          emailAttendeeCertificates(
            [attendee],
            Boolean(attendee.certificateSentAt),
          ),
      });
    },
    [emailAttendeeCertificates, t],
  );

  const confirmEmailCertificates = useCallback(
    (attendees: CertificateActionAttendee[]) => {
      const count = emailable(attendees, false).length;
      if (count === 0) {
        message.info(
          t('components.OpenClassAttendeeActions.emailCertificatesNoneLeft'),
        );
        return;
      }
      Modal.confirm({
        content: t(
          'components.OpenClassAttendeeActions.emailCertificatesConfirm',
          { count },
        ),
        okText: t('common.confirm'),
        title: t('components.OpenClassAttendeeActions.emailCertificates', {
          count,
        }),
        onOk: () => emailAttendeeCertificates(attendees),
      });
    },
    [emailAttendeeCertificates, emailable, t],
  );

  return {
    besaccPreview,
    canIssueCertificate,
    cancelLanguagePrompt: () => setLanguagePrompt(undefined),
    certificateLanguages: standardTemplateOf(openClass)?.languages ?? [],
    closeBesaccPreview: () => setBesaccPreviewId(undefined),
    confirmLanguagePrompt,
    confirmEmailCertificate,
    confirmEmailCertificates,
    downloadCertificates,
    languagePromptOpen: languagePrompt != null,
    previewBesaccCertificate: (attendee: CertificateActionAttendee) =>
      setBesaccPreviewId(attendee.registrationId),
    removeBesaccCertificate,
    runUpdates,
    saving,
    uploadBesaccCertificate,
  };
};
